// File: frontend/src/components/Category/CategoryForm.tsx
// Converted from CategoryModal to standalone form component

import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  AlertCircle, 
  RefreshCw,
  FolderTree,
  Tag,
  Hash,
  Eye,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { CategoryFormData } from '@/lib/api/categoryService';

interface ParentCategory {
  category_id: number;
  name: string;
  icon?: string;
  slug_url?: string;
}

interface Category {
  category_id: number;
  name: string;
  description?: string;
  icon?: string;
  parent_category_id?: number | null;
  display_order?: number;
  is_active?: boolean;
  slug_url?: string;
  created_at?: string;
  updated_at?: string;
  subcategory_count?: number;
}

interface CategoryFormProps {
  category?: Category | null;
  parentCategories: ParentCategory[];
  onSubmit: (data: CategoryFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  className?: string;
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  parentCategories,
  onSubmit,
  onCancel,
  isSubmitting = false,
  className = ''
}) => {
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    icon: '',
    parent_category_id: null,
    display_order: 0,
    is_active: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Initialize form data when category changes
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        icon: category.icon || '',
        parent_category_id: category.parent_category_id || null,
        display_order: category.display_order || 0,
        is_active: category.is_active ?? true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        icon: '',
        parent_category_id: null,
        display_order: 0,
        is_active: true
      });
    }
    
    // Reset errors when category changes
    setErrors({});
    setTouched({});
  }, [category]);

  // Validation
  const validateField = (name: string, value: any) => {
    switch (name) {
      case 'name':
        if (!value || value.trim().length === 0) {
          return 'Category name is required';
        }
        if (value.trim().length < 2) {
          return 'Category name must be at least 2 characters';
        }
        if (value.trim().length > 100) {
          return 'Category name must be less than 100 characters';
        }
        return '';
      
      case 'description':
        if (value && value.length > 500) {
          return 'Description must be less than 500 characters';
        }
        return '';
      
      case 'icon':
        if (value && !value.match(/^[a-zA-Z0-9\s\-_]+$/)) {
          return 'Icon class should contain only letters, numbers, spaces, hyphens, and underscores';
        }
        return '';
      
      case 'display_order':
        if (value < 0) {
          return 'Display order must be 0 or greater';
        }
        return '';
      
      default:
        return '';
    }
  };

  const handleInputChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate field if it has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleInputBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof CategoryFormData]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof CategoryFormData]);
      if (error) {
        newErrors[key] = error;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const getFieldError = (fieldName: string) => {
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : '';
  };

  const isFormValid = Object.keys(errors).length === 0 && formData.name.trim().length > 0;

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FolderTree className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              {category ? 'Edit Category' : 'Create New Category'}
            </h3>
          </div>
          
          {isSubmitting && (
            <div className="flex items-center text-sm text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </div>
          )}
        </div>
        
        <p className="text-sm text-gray-600 mt-1">
          {category 
            ? 'Update category information and settings' 
            : 'Add a new category to organize your services'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Information Section */}
        <div className="space-y-4">
          <div className="flex items-center pb-2 border-b border-gray-200">
            <Tag className="h-4 w-4 text-gray-500 mr-2" />
            <h4 className="text-md font-medium text-gray-900">Basic Information</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Name */}
            <div className="md:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Category Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                onBlur={() => handleInputBlur('name')}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 text-sm ${
                  getFieldError('name')
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="Enter category name"
                disabled={isSubmitting}
              />
              {getFieldError('name') && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {getFieldError('name')}
                </p>
              )}
            </div>
            
            {/* Icon Class */}
            <div className="md:col-span-1">
              <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-1">
                Icon Class
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                  onBlur={() => handleInputBlur('icon')}
                  className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 text-sm ${
                    getFieldError('icon')
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="e.g., fas fa-home"
                  disabled={isSubmitting}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {formData.icon ? (
                    <i className={`${formData.icon} text-gray-400`}></i>
                  ) : (
                    <ImageIcon className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Use Font Awesome classes (e.g., fas fa-home, fas fa-car)
              </p>
              {getFieldError('icon') && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {getFieldError('icon')}
                </p>
              )}
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              onBlur={() => handleInputBlur('description')}
              rows={3}
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 text-sm ${
                getFieldError('description')
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter category description (optional)"
              disabled={isSubmitting}
            />
            <div className="mt-1 flex items-center justify-between">
              <div>
                {getFieldError('description') && (
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {getFieldError('description')}
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {formData.description?.length || 0}/500 characters
              </p>
            </div>
          </div>
        </div>

        {/* Category Settings Section */}
        <div className="space-y-4">
          <div className="flex items-center pb-2 border-b border-gray-200">
            <Hash className="h-4 w-4 text-gray-500 mr-2" />
            <h4 className="text-md font-medium text-gray-900">Category Settings</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Parent Category */}
            <div>
              <label htmlFor="parent_category_id" className="block text-sm font-medium text-gray-700 mb-1">
                Parent Category
              </label>
              <select
                id="parent_category_id"
                value={formData.parent_category_id || ''}
                onChange={(e) => handleInputChange('parent_category_id', e.target.value ? Number(e.target.value) : null)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={isSubmitting}
              >
                <option value="">No parent (Top level category)</option>
                {parentCategories.map((parent) => (
                  <option key={parent.category_id} value={parent.category_id}>
                    {parent.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select a parent category to create a subcategory
              </p>
            </div>
            
            {/* Display Order */}
            <div>
              <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                type="number"
                id="display_order"
                value={formData.display_order}
                onChange={(e) => handleInputChange('display_order', Number(e.target.value) || 0)}
                onBlur={() => handleInputBlur('display_order')}
                min="0"
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 text-sm ${
                  getFieldError('display_order')
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="0"
                disabled={isSubmitting}
              />
              <p className="mt-1 text-xs text-gray-500">
                Lower numbers appear first in the listing
              </p>
              {getFieldError('display_order') && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {getFieldError('display_order')}
                </p>
              )}
            </div>
          </div>
          
          {/* Status Toggle */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={isSubmitting}
            />
            <label htmlFor="is_active" className="flex items-center text-sm text-gray-900">
              <Eye className="h-4 w-4 mr-2 text-gray-500" />
              Active (Category will be visible to users)
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {category ? 'Update Category' : 'Create Category'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;